import {useEffect, useRef} from "react";

import {ControlButton} from "@ui/buttons/ControlButton";
import {RootButton} from "@ui/buttons/RootButton";
import {useTranslation} from "react-i18next";

import styles from "../WebRTCSupportPopup/styles.module.scss";

export const MicrophonePermissionPopup = ({
  requestMicrophoneAccess,
  continueWithAvatar,
  continueWithoutAvatar,
}) => {
  const {t} = useTranslation();
  const hasAutoRequestedRef = useRef(false);
  const isResolvingRef = useRef(false);

  const tryRequestAndContinue = async () => {
    if (isResolvingRef.current) return false;
    isResolvingRef.current = true;

    try {
      const isGranted = await requestMicrophoneAccess();
      if (isGranted) {
        await continueWithAvatar();
        return true;
      }

      return false;
    } finally {
      isResolvingRef.current = false;
    }
  };

  useEffect(() => {
    if (
      hasAutoRequestedRef.current ||
      typeof requestMicrophoneAccess !== "function"
    )
      return;
    hasAutoRequestedRef.current = true;

    let isMounted = true;

    const request = async () => {
      const isGranted = await tryRequestAndContinue();

      if (isMounted && isGranted) {
        return;
      }
    };

    request();

    const retryOnUserGesture = async () => {
      if (!isMounted) return;
      const isGranted = await tryRequestAndContinue();
      if (isGranted) {
        window.removeEventListener("pointerdown", retryOnUserGesture);
        window.removeEventListener("keydown", retryOnUserGesture);
      }
    };

    window.addEventListener("pointerdown", retryOnUserGesture, {once: true});
    window.addEventListener("keydown", retryOnUserGesture, {once: true});

    return () => {
      isMounted = false;
      window.removeEventListener("pointerdown", retryOnUserGesture);
      window.removeEventListener("keydown", retryOnUserGesture);
    };
  }, [requestMicrophoneAccess, continueWithAvatar]);

  return (
    <div className={styles.webrtcSupportPopup}>
      <h2>{t("popup.microphone.title")}</h2>
      <h2>{t("popup.microphone.description")}</h2>

      <div className={styles.webrtcSupportBtns}>
        <ControlButton
          color={"var(--violet)"}
          onClick={continueWithoutAvatar}
          txt={t("button.continue_without_avatar")}
        />
        <RootButton
          type={"button"}
          onClick={tryRequestAndContinue}
          txt={t("button.continue_avatar")}
        />
      </div>
    </div>
  );
};
